// Root component — orchestrates routing, RBAC, tenants, users, and the shells.
// Previously lived inline inside index.html inside a <script type="text/babel"> block.
import React, { useState, useEffect } from 'react';

import { Icon } from './ui.jsx';
import { REPS, RUNS, INVOICES, CLIENTS } from './data.jsx';
import { AdminShell, RepShell } from './shells.jsx';
import { LoginScreen, PasswordResetScreen, NotAuthorizedScreen, canAccess, defaultScreenFor } from './auth.jsx';
import { LandingPage } from './LandingPage.jsx';
import { Tutorial } from './Tutorial.jsx';
import { loadUsers, mirrorRepsToWindow, ScreenUsers, genRepId } from './users.jsx';
import { ScreenUserRoles } from './userRoles.jsx';
import { loadTenants, saveTenant as saveTenantRemote, getActiveTenantId, setActiveTenantId, ScreenTenantsList, ScreenNewTenant, ScreenEditTenant } from './tenants.jsx';
import { api } from './api.js';
import {
  ScreenAdminDashboard, ScreenRunsList, ScreenNewRunSetup,
  ScreenUploadTxn, ScreenUploadAging, ScreenAssign,
} from './admin.jsx';
import { ScreenRunDetail, InvoiceDrawer, ScreenCustomers, ScreenRates } from './rundetail.jsx';
import { ScreenCommissionPlans } from './commissionPlans.jsx';
import { HelpPage } from './HelpPage.jsx';
import { ScreenRepDashboard, ScreenRepHistory, ScreenRepUnpaid } from './rep.jsx';

// Dev-only defaults. Hot-tip: this whole constant disappears in production builds
// once we gate it behind `import.meta.env.DEV` — Vite strips dead code.
const TWEAKS = { role: 'admin', screen: 's02', runStatus: 'draft', density: 'comfortable' };

export function App() {
  const savedScreen = localStorage.getItem('sc_screen');
  const savedStatus = localStorage.getItem('sc_status');

  // === Auth state machine ===
  // authUser  = Supabase auth user (email, id), or null if signed out.
  // appUser   = row from public.users (role, tenant, rep_id, name), or null
  //             if signed in but not in the users table (= not authorized).
  // authPhase = 'loading' | 'signed-out' | 'not-authorized' | 'ready'
  const [authUser, setAuthUser] = useState(null);
  const [appUser,  setAppUser]  = useState(null);
  const [authPhase, setAuthPhase] = useState('loading');

  // ---------- Path-based routing ----------
  // `/`                          → public marketing landing page
  // `/login`                     → login screen (if signed out)
  // `/dashboard`, `/rates`, etc. → authenticated app screens
  //
  // SCREEN_TO_PATH maps internal screen ids (s02 etc.) to human URLs.
  // PATH_TO_SCREEN is the inverse — used when the browser URL changes
  // (back/forward, direct link) so we can restore the right screen.
  const SCREEN_TO_PATH = {
    s02: '/dashboard',
    s03: '/commission-runs',
    s04: '/commission-runs/new',
    s05: '/commission-runs/new/upload-transaction',
    s06: '/commission-runs/new/upload-aging',
    s07: '/commission-runs/new/assign',
    s08: '/commission-runs/detail',
    s09: '/commission-runs/detail',
    s11: '/customers',
    s11u: '/customers/unassigned',
    s13: '/users/sales-reps',
    // /rates is kept as an alias for backward-compat; canonical is /commission-plans.
    s14: '/commission-plans',
    s23: '/commission-plans',
    s24: '/help',
    s14s: '/rates',
    s19: '/audit',
    s20: '/tenants',
    s21: '/tenants/new',
    s25: '/tenants/edit',
    s26: '/users/roles',
    s22: '/users',
    s15: '/rep/this-month',
    s16: '/rep/by-period',
    s17: '/rep/history',
    s18: '/rep/unpaid',
    s00: '/login',
  };
  // Build the inverse — longest-match wins for nested paths.
  const PATH_TO_SCREEN = Object.fromEntries(Object.entries(SCREEN_TO_PATH).map(([k, v]) => [v, k]));
  const screenForPath = (p) => PATH_TO_SCREEN[p.replace(/\/+$/, '') || '/'] || null;

  const [path, setPath] = useState(() => window.location.pathname + window.location.search + window.location.hash);
  useEffect(() => {
    const handler = () => setPath(window.location.pathname + window.location.search + window.location.hash);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);
  const navigateTo = (newPath) => {
    window.history.pushState({}, '', newPath);
    setPath(newPath);
  };
  const isLandingRoute = () => {
    const p = path.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
    return p === '/';
  };

  // Password-reset: Supabase appends ?type=recovery (or &type=recovery in the
  // hash fragment) to the redirect URL when the user clicks a reset email.
  // We show the PasswordResetScreen until they finish setting a new password.
  const [resetMode, setResetMode] = useState(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const qs   = new URLSearchParams(window.location.search);
    return hash.get('type') === 'recovery' || qs.get('type') === 'recovery';
  });

  const role = appUser ? appUser.role : 'admin';

  const [screen, setScreen] = useState('s00');
  const [runStatus, setRunStatus] = useState(savedStatus || TWEAKS.runStatus);

  // Tutorial visibility + first-time auto-open
  const [tutorialOpen, setTutorialOpen] = useState(false);
  useEffect(() => {
    if (authPhase !== 'ready' || appUser?.role !== 'admin') return;
    if (localStorage.getItem('sc_tutorial_seen')) return;
    // Auto-open once per user, a beat after the dashboard mounts.
    const t = setTimeout(() => setTutorialOpen(true), 800);
    return () => clearTimeout(t);
  }, [authPhase, appUser?.role]);
  const closeTutorial = () => {
    localStorage.setItem('sc_tutorial_seen', '1');
    setTutorialOpen(false);
  };

  // Logout uses a hard reload to /login so we never end up with a stale
  // mounted screen (e.g. ScreenCommissionPlans) trying to fetch with an
  // expired session and rendering an empty/error state. Cleanest reset.
  const logout = async () => {
    try { await api.auth.signOut(); } catch (e) { console.warn('signOut error:', e); }
    window.location.assign('/login');
  };

  // Resolve the app user whenever the auth user changes. This is where the
  // email -> public.users lookup happens, plus the first-login auth_sub claim.
  const resolveAppUser = async (au) => {
    if (!au) { setAppUser(null); setAuthPhase('signed-out'); return; }
    try {
      let row = await api.users.getByEmail(au.email);
      if (!row) { setAppUser(null); setAuthPhase('not-authorized'); return; }
      // First login — stamp auth_sub onto the row so future RLS policies work.
      if (!row.authSub) {
        try { row = await api.users.claimAuthSub(row.id, au.id); } catch (e) { console.warn('auth_sub claim failed:', e); }
      }
      // RBAC: resolve the user's role permissions so canAccess /
      // hasPermission can gate every screen and action. Falls back
      // gracefully if the migration hasn't run yet.
      try {
        const roles = await api.roles.list();
        const myRole = roles.find(r => r.id === row.roleId)
                    || roles.find(r => r.isSystemRole && r.baseRole === row.role);
        if (myRole) {
          row = { ...row, role: row.role, baseRole: myRole.baseRole, roleName: myRole.name, permissions: myRole.permissions || {} };
        }
      } catch (e) { console.warn('Could not resolve role permissions:', e); }
      setAppUser(row);
      setAuthPhase('ready');
      // If the user landed on a specific URL (e.g. /rates, /users), honor it.
      // Else fall back to their default screen (dashboard for admin, this-month for rep).
      const cleanPath = window.location.pathname.replace(/\/+$/, '') || '/';
      const fromUrl = screenForPath(cleanPath);
      const target = fromUrl && canAccess(row, fromUrl) ? fromUrl : defaultScreenFor(row);
      setScreen(target);
      const targetPath = SCREEN_TO_PATH[target] || '/dashboard';
      if (window.location.pathname !== targetPath) {
        window.history.replaceState({}, '', targetPath);
        setPath(targetPath);
      }
    } catch (err) {
      console.error('Could not resolve app user:', err);
      setAppUser(null); setAuthPhase('not-authorized');
    }
  };

  useEffect(() => {
    // On mount: check for an existing session (e.g. persisted from last visit).
    api.auth.current().then(au => { setAuthUser(au); resolveAppUser(au); });
    // Subscribe: fires on magic-link redirect, sign-in, sign-out, token refresh.
    const unsub = api.auth.onChange((au) => { setAuthUser(au); resolveAppUser(au); });
    return unsub;
  }, []);

  const [invoices, setInvoices]   = useState(INVOICES);
  // Live customer list from Supabase. Used for the unassigned badge in the sidebar.
  // NOTE: the Customers screen fetches its own copy — this is only for the badge count.
  const [customers, setCustomers] = useState([]);
  const [drawerInv, setDrawerInv] = useState(null);
  const [wizard, setWizard]       = useState({});
  const [tweaksOpen, setTweaksOpen] = useState(false);

  // Tenants are fetched from Supabase on mount (via loadTenants() → api.tenants.list()).
  // While the request is in flight, tenants is [] and activeTenant is undefined —
  // screens that need a tenant show a tiny loading state.
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [activeTenantId, setActiveTenantIdState] = useState(getActiveTenantId());

  // Fetch tenants ONCE the app user is resolved — before that, RLS rejects the
  // query (current_tenant_id() is NULL until auth_sub is claimed).
  useEffect(() => {
    if (authPhase !== 'ready') return;
    let alive = true;
    setTenantsLoading(true);
    loadTenants()
      .then(ts => {
        if (!alive) return;
        setTenants(ts);
        if (!getActiveTenantId() && ts[0]) {
          setActiveTenantId(ts[0].id);
          setActiveTenantIdState(ts[0].id);
        }
      })
      .catch(err => console.error('Failed to load tenants:', err))
      .finally(() => { if (alive) setTenantsLoading(false); });
    return () => { alive = false; };
  }, [authPhase]);

  const activeTenant = tenants.find(t => t.id === activeTenantId) || tenants[0];
  // Switch the active tenant — local state + localStorage + server-side
  // user_active_tenant (so RLS scopes follow). Triggers a re-resolve of
  // the app user (their role can differ per tenant).
  const selectTenant = async (id) => {
    setActiveTenantId(id);
    setActiveTenantIdState(id);
    try {
      await api.userActiveTenant.set(id);
      // Re-resolve the app user against the new active tenant so role +
      // permissions are correct for the picked context.
      if (authUser) await resolveAppUser(authUser);
    } catch (err) {
      console.warn('Could not persist active tenant:', err);
    }
  };

  const saveTenant = async (t) => {
    const saved = await saveTenantRemote(t);
    const isNew = !t.id;
    setTenants(prev => {
      const without = prev.filter(x => x.id !== saved.id);
      return [...without, saved];
    });
    // If the platform admin just CREATED a new tenant (vs. edited an
    // existing one), give them an admin user row in that tenant so they
    // can manage it. The auth_sub UNIQUE constraint was dropped in 0038,
    // so the same auth user can hold rows in multiple tenants.
    if (isNew && appUser?.platformAdmin && authUser) {
      try {
        await api.users.create({
          tenantId:  saved.id,
          authSub:   authUser.id,
          email:     appUser.email,
          name:      appUser.name,
          role:      'admin',
          active:    true,
        });
      } catch (err) {
        // Conflict here usually means a row already existed — ignore.
        console.warn('Auto-seed platform admin in new tenant:', err);
      }
    }
    selectTenant(saved.id);
  };

  const [users, setUsersState] = useState([]);

  // Load users when the session is ready OR the active tenant changes.
  // Without the activeTenantId dep, switching tenants would keep showing
  // the previous tenant's user list — RLS rescoped server-side but the
  // React state held stale rows.
  useEffect(() => {
    if (authPhase !== 'ready') return;
    loadUsers()
      .then(us => { setUsersState(us); mirrorRepsToWindow(us); })
      .catch(err => console.error('Failed to load users:', err));
  }, [authPhase, activeTenantId]);

  const addUser = async (data) => {
    // Invite flow: edge function creates both the auth user AND the app row
    // using service_role. Returns { user, tempPassword, message } so the
    // modal can show the temp password to the admin.
    const payload = {
      email: data.email,
      name: data.name,
      role: data.role,
      repId: data.role === 'rep' ? genRepId() : null,
    };
    const result = await api.users.invite(payload);
    const next = [...users, result.user];
    setUsersState(next); mirrorRepsToWindow(next);
    return result; // modal uses this to display tempPassword
  };
  const editUser = async (id, data) => {
    const saved = await api.users.update(id, data);
    const next = users.map(u => u.id === id ? saved : u);
    setUsersState(next); mirrorRepsToWindow(next);
  };
  const toggleUserActive = async (id) => {
    const target = users.find(u => u.id === id);
    if (!target) return;
    const saved = await api.users.update(id, { active: !target.active });
    const next = users.map(u => u.id === id ? saved : u);
    setUsersState(next); mirrorRepsToWindow(next);
  };

  const archiveUser = async (id) => {
    const saved = await api.users.archive(id, appUser?.id || null);
    const next = users.map(u => u.id === id ? saved : u);
    setUsersState(next); mirrorRepsToWindow(next);
  };
  const unarchiveUser = async (id) => {
    const saved = await api.users.unarchive(id);
    const next = users.map(u => u.id === id ? saved : u);
    setUsersState(next); mirrorRepsToWindow(next);
  };

  // Fetch customers when session is ready OR active tenant changes —
  // same staleness fix as the users effect above.
  useEffect(() => {
    if (authPhase !== 'ready') return;
    api.customers.list().then(setCustomers).catch(err => console.error('customers badge load:', err));
  }, [authPhase, activeTenantId]);
  const unassignedCount = customers.filter(c => !c.repUserId).length;
  // The signed-in rep's identity. Rep views query invoices where rep_user_id = currentRep.id.
  const currentRep = appUser && appUser.role === 'rep'
    ? { id: appUser.id, name: appUser.name, email: appUser.email, y1Rate: appUser.y1Rate, y2Rate: appUser.y2Rate }
    : REPS[0];

  useEffect(() => {
    localStorage.setItem('sc_screen', screen);
    localStorage.setItem('sc_role', role);
    localStorage.setItem('sc_status', runStatus);
  }, [screen, role, runStatus]);

  const nav = (s) => {
    if (!canAccess(appUser, s)) { s = defaultScreenFor(appUser); }
    setScreen(s);
    const targetPath = SCREEN_TO_PATH[s] || '/dashboard';
    if (window.location.pathname !== targetPath) {
      window.history.pushState({}, '', targetPath);
      setPath(targetPath + window.location.search + window.location.hash);
    }
  };

  // Reverse: when the URL changes (back/forward/direct visit), sync the screen.
  useEffect(() => {
    if (authPhase !== 'ready') return;
    const cleanPath = path.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
    if (cleanPath === '/') return; // landing, handled separately
    const s = screenForPath(cleanPath);
    if (s && s !== screen && canAccess(appUser, s)) {
      setScreen(s);
    }
  }, [path, authPhase]);

  const onOverride = (id, newComm, reason) => {
    setInvoices(invoices.map(i => i.id === id ? {
      ...i, originalCommission: i.commission, commission: newComm, overridden: true, overrideReason: reason,
    } : i));
  };

  const renderAdmin = () => {
    switch (screen) {
      case 's02': return <ScreenAdminDashboard onNav={nav} tenant={activeTenant} currentUser={appUser}/>;
      case 's03': return <ScreenRunsList onNav={nav} currentUser={appUser}/>;
      case 's04': return <ScreenNewRunSetup onNav={nav} wizard={wizard} setWizard={setWizard} tenant={activeTenant} currentUser={appUser}/>;
      case 's05': return <ScreenUploadTxn onNav={nav} wizard={wizard} setWizard={setWizard} tenant={activeTenant}/>;
      case 's06': return <ScreenUploadAging onNav={nav} wizard={wizard} setWizard={setWizard} tenant={activeTenant}/>;
      case 's20': return <ScreenTenantsList tenants={tenants} activeId={activeTenantId} onNav={nav} onSelect={selectTenant} currentUser={appUser}/>;
      case 's21': return <ScreenNewTenant onNav={nav} onSave={(t) => { saveTenant(t); nav('s20'); }}/>;
      case 's25': return <ScreenEditTenant tenants={tenants} activeId={activeTenantId} onNav={nav} onSave={saveTenant}/>;
      case 's07': return <ScreenAssign onNav={nav} wizard={wizard}/>;
      case 's08':
      case 's09': return <ScreenRunDetail onNav={nav} onOpenInvoice={setDrawerInv} currentUser={appUser}/>;
      case 's11':
      case 's11u': return <ScreenCustomers onNav={nav} currentUser={appUser}/>;
      case 's13':
      case 's22': return <ScreenUsers users={users} onAdd={addUser} onEdit={editUser} onToggleActive={toggleUserActive} onArchive={archiveUser} onUnarchive={unarchiveUser} onNav={nav} currentUser={appUser}/>;
      // "View as rep" preview — admins can hit the rep screens with ?as=<id>.
      // Permission check (own_view.read) is already satisfied by admin's all:true.
      case 's15':
      case 's16': return <ScreenRepDashboard rep={null} currentUser={appUser} onOpenInvoice={setDrawerInv} tenant={activeTenant}/>;
      case 's26': return <ScreenUserRoles onNav={nav} currentUser={appUser}/>;
      // /rates is now an alias for /commission-plans — the legacy ScreenRates
      // component is retained but no longer reachable from the nav.
      case 's14':
      case 's14s':
      case 's23': return <ScreenCommissionPlans onNav={nav} currentUser={appUser}/>;
      case 's24': return <HelpPage onNav={nav} onStartTour={() => setTutorialOpen(true)}/>;
      case 's19': return <div className="text-neutral-500 text-sm">Audit log — timestamped immutable record.</div>;
      default: return <ScreenAdminDashboard onNav={nav} tenant={activeTenant} currentUser={appUser}/>;
    }
  };

  const renderRep = () => {
    switch (screen) {
      case 's15': return <ScreenRepDashboard rep={currentRep} currentUser={appUser} onOpenInvoice={setDrawerInv} tenant={activeTenant}/>;
      case 's16': return <ScreenRepDashboard rep={currentRep} currentUser={appUser} onOpenInvoice={setDrawerInv} tenant={activeTenant}/>;
      case 's17': return <ScreenRepHistory rep={currentRep}/>;
      case 's18': return <ScreenRepUnpaid rep={currentRep} onOpenInvoice={setDrawerInv}/>;
      default: return <ScreenRepDashboard rep={currentRep} onOpenInvoice={setDrawerInv} tenant={activeTenant}/>;
    }
  };

  // Public landing page on `/` — no auth required, no data fetching.
  if (isLandingRoute()) {
    return <LandingPage onSignIn={() => navigateTo(authPhase === 'ready' ? (SCREEN_TO_PATH[defaultScreenFor(appUser)] || '/dashboard') : '/login')} isSignedIn={authPhase === 'ready'}/>;
  }

  if (authPhase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center text-[13px] text-neutral-500">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-primary-700 border-t-transparent rounded-full animate-spin"/>
          Checking session…
        </div>
      </div>
    );
  }
  // Password reset flow takes priority — user is mid-recovery.
  if (resetMode && authUser) {
    return <PasswordResetScreen onDone={() => {
      // Clear the ?type=recovery flag from the URL and continue normally.
      window.history.replaceState({}, '', window.location.pathname);
      setResetMode(false);
    }}/>;
  }
  if (authPhase === 'signed-out') return <LoginScreen/>;
  if (authPhase === 'not-authorized') return <NotAuthorizedScreen email={authUser?.email} onSignOut={logout}/>;

  if (tenantsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[13px] text-neutral-500">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-primary-700 border-t-transparent rounded-full animate-spin"/>
          Loading tenants…
        </div>
      </div>
    );
  }

  return (
    <div className="h-full">
      {role === 'admin'
        ? <AdminShell current={screen} onNav={nav} unassignedCount={unassignedCount}
            tenants={tenants} activeTenantId={activeTenantId} onSelectTenant={selectTenant} onNewTenant={() => nav('s21')}
            user={appUser} onLogout={logout}
            onHelp={() => nav('s24')}
            onTenantUpdated={(updated) => setTenants(ts => ts.map(t => t.id === updated.id ? updated : t))}>
            {renderAdmin()}
          </AdminShell>
        : <RepShell current={screen} onNav={nav} repName={appUser.name} user={appUser} tenant={activeTenant} onLogout={logout}>{renderRep()}</RepShell>}

      <InvoiceDrawer invoice={drawerInv} onClose={() => setDrawerInv(null)} onOverride={onOverride} readOnly={role === 'rep' || runStatus === 'finalized'}/>

      <Tutorial open={tutorialOpen} onClose={closeTutorial} onNav={nav}/>
    </div>
  );
}
